import Loader from '@components/common/Loader';
import type { Metric } from '@enums';
import { useInsightsQuery } from '@hooks';
import { DEFAULT_METRIC, getInsightChartMinWidth } from '@utils';
import type { RowState, RowSuccessState } from 'hooks/useInsightsQuery';
import type { ChartType } from 'types/common';
import InsightChartContent from './InsightChartContent';
import Styled from './InsightChartsBlock.styled';

export interface InsightChartsBlockProps {
  insightIds: string[];
  type: ChartType;
  metric?: Metric;
  layout?: 'vertical' | 'horizontal';
}

function InsightChartsBlock({
  insightIds,
  type,
  metric,
  layout = 'vertical'
}: InsightChartsBlockProps) {
  const chartMetric = metric ?? DEFAULT_METRIC[type];
  const { rows } = useInsightsQuery(insightIds, chartMetric);
  const minWidth = getInsightChartMinWidth(type);

  const getContent = (row: RowState) => {
    if (row.state === 'loading') {
      return (
        <Styled.LoadingContainer>
          <Loader />
        </Styled.LoadingContainer>
      );
    }

    if (row.state === 'error') {
      return (
        <Styled.ErrorText>
          Insight {row.id}: {row.message}
        </Styled.ErrorText>
      );
    }

    const { series, insight } = row as RowSuccessState;

    return (
      <InsightChartContent
        type={type}
        metric={chartMetric}
        insight={insight}
        series={series}
      />
    );
  };

  return (
    <Styled.Block layout={layout} data-testid="insight-charts">
      {rows.map((row, index) => {
        const key = `${row.state}-${'id' in row ? row.id : index}`;

        return (
          <Styled.ChartCard
            key={key}
            layout={layout}
            minWidth={minWidth}
            widthPercent={layout === 'horizontal' ? 50 : undefined}>
            {getContent(row)}
          </Styled.ChartCard>
        );
      })}
    </Styled.Block>
  );
}

export default InsightChartsBlock;
